import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {object} DraperyPageModalRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} content
 */

/**
 * Shows a store page in a modal, so the shopper stays on the product page.
 *
 * A normal link points at the component with on:click="#<id>/openPage" and, for
 * a faster open, on:pointerenter="#<id>/prefetch". The first open fetches the
 * page in data-url and moves the sections in its <main> into the dialog.
 * Later opens show the same nodes again.
 *
 * Clicks with a modifier key keep the link behaviour (new tab, new window).
 * If the page cannot be loaded, the component goes to the page instead.
 * @extends {DialogComponent}
 */
class DraperyPageModalComponent extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  /** @type {Promise<Document> | null} */
  #page = null;

  /** @type {Promise<void> | null} */
  #render = null;

  /** @param {MouseEvent} event */
  openPage(event) {
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    this.showDialog();
    this.#renderPage().catch((error) => {
      console.error(error);
      window.location.assign(this.dataset.url ?? '/');
    });
  }

  prefetch() {
    if (this.#hasPage()) return;
    this.#fetchPage().catch(() => {});
  }

  /**
   * Read from the DOM, not a flag: if a re-render of <main> resets the dialog,
   * the next open loads the page again.
   */
  #hasPage() {
    return this.refs.content.querySelector(':scope > .shopify-section') !== null;
  }

  /** @returns {Promise<Document>} */
  #fetchPage() {
    const url = this.dataset.url ?? '';

    this.#page ??= fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`${response.status} for ${url}`);
        return response.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html'))
      .catch((error) => {
        this.#page = null;
        throw error;
      });

    return this.#page;
  }

  /** @returns {Promise<void>} */
  #renderPage() {
    if (this.#hasPage()) return Promise.resolve();

    this.#render ??= this.#fetchPage()
      .then((page) => {
        // Only the sections are kept, so the rest of the parsed page can go
        this.#page = null;
        const sections = page.querySelectorAll('main .shopify-section');
        if (!sections.length) throw new Error(`No sections in ${this.dataset.url}`);

        const { content, dialog } = this.refs;
        content.replaceChildren(...sections);
        runScripts(content);
        dialog.removeAttribute('aria-busy');
      })
      .finally(() => {
        this.#render = null;
      });

    return this.#render;
  }
}

/**
 * Scripts that come in from a parsed page do not run, so each one is swapped
 * for a new copy. Section scripts often wait for DOMContentLoaded, which has
 * already fired. While the inline scripts run, those listeners are called
 * straight away instead. Scripts with src run later and get no such help.
 * @param {HTMLElement} container
 */
function runScripts(container) {
  const addListener = document.addEventListener;

  /** @type {typeof document.addEventListener} */
  const addListenerNow = function (type, listener, options) {
    if (type !== 'DOMContentLoaded' || !listener) return addListener.call(this, type, listener, options);

    const event = new Event(type);
    queueMicrotask(() => (typeof listener === 'function' ? listener.call(document, event) : listener.handleEvent(event)));
  };

  document.addEventListener = addListenerNow;
  try {
    for (const oldScript of container.querySelectorAll('script')) {
      const script = document.createElement('script');
      for (const { name, value } of oldScript.attributes) script.setAttribute(name, value);
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    }
  } finally {
    document.addEventListener = addListener;
  }
}

if (!customElements.get('drapery-page-modal-component')) {
  customElements.define('drapery-page-modal-component', DraperyPageModalComponent);
}
